import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';

import { Button } from '@/components/wpl/button/src/button';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

/**
 * Map of known atlassian.com product paths to product names.
 * Entries are ordered longest-path-first so that `/software/jira/service-management`
 * matches before `/software/jira`.
 */
const PRODUCT_PATH_MAP: { prefix: string; name: string; signupUrl: string }[] = [
	{
		prefix: '/software/jira/service-management',
		name: 'Jira Service Management',
		signupUrl: '/try/cloud/signup?bundle=service-collection',
	},
	{
		prefix: '/software/jira/product-discovery',
		name: 'Jira Product Discovery',
		signupUrl: '/try/cloud/signup?bundle=jira-product-discovery',
	},
	{
		prefix: '/software/confluence',
		name: 'Confluence',
		signupUrl: '/try/cloud/signup?bundle=confluence',
	},
	{ prefix: '/software/jira', name: 'Jira', signupUrl: '/try/cloud/signup?bundle=jira' },
	{
		prefix: '/collections/teamwork',
		name: 'Teamwork Collection',
		signupUrl: '/try/cloud/signup?bundle=teamwork-collection',
	},
];

/**
 * Extract a product name from a search result URL by matching the URL path
 * against known product path prefixes. Longer prefixes are checked first so
 * that sub-products (e.g. Jira Service Management) take priority over their
 * parent product (Jira).
 */
export const extractProductFromUrl = (url: string): { name: string; signupUrl: string } | null => {
	if (!url) {
		return null;
	}

	try {
		const pathname = new URL(url).pathname.toLowerCase().replace(/\/+$/, '');
		for (const entry of PRODUCT_PATH_MAP) {
			if (pathname === entry.prefix) {
				return { name: entry.name, signupUrl: entry.signupUrl };
			}
		}
	} catch {
		// If the URL is relative or malformed, try matching against the raw string
		const lowerUrl = url.toLowerCase().replace(/\/+$/, '');
		for (const entry of PRODUCT_PATH_MAP) {
			if (lowerUrl === entry.prefix) {
				return { name: entry.name, signupUrl: entry.signupUrl };
			}
		}
	}

	return null;
};

type SearchResultTryButtonProps = {
	resultUrl: string;
	searchQuery: string;
	resultIndex: number;
};

export const SearchResultTryButton = ({
	resultUrl,
	searchQuery,
	resultIndex,
}: SearchResultTryButtonProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const product = extractProductFromUrl(resultUrl);

	// Hook must be called unconditionally (before the early return).
	// The fallback value is unused because we return null when product is absent.
	const tryHref = useLocalizedHref(product?.signupUrl ?? '/try/cloud/signup');

	if (!product) {
		return null;
	}

	const buttonText = intl.formatMessage(
		{
			id: 'wac.search.result.try.button',
			defaultMessage: 'Try {productName}',
		},
		{ productName: product.name },
	);

	const handleClick = () => {
		createAnalyticsEvent({
			eventName: 'searchResultTryButton',
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'searchResultTryButton',
			source: 'searchPage',
			eventContainer: 'searchResults',
			attributes: {
				searchQuery,
				productName: product.name,
				signupUrl: product.signupUrl,
				resultIndex,
				resultUrl,
				destinationUrl: tryHref,
			},
		}).fire();
	};

	return (
		<div css={tryButtonWrapperCss} data-testid={`search-result-try-button-${resultIndex}`}>
			<Button
				text={buttonText}
				url={tryHref}
				type="primary"
				css={tryButtonCss}
				aria-label={buttonText}
				onClick={handleClick}
				dataEventProps={{
					'data-event-component': 'linkButton',
					'data-event-container': 'searchResult',
					'data-product-key': 'search',
					'data-label': 'search-result-try-button',
					'data-event': 'clicked',
				}}
			/>
		</div>
	);
};

const tryButtonWrapperCss = css({
	marginBlockStart: '8px',
});

const tryButtonCss = css({
	fontSize: '1rem',
	fontWeight: 500,
	height: '2rem',
	width: 'auto',
	borderRadius: '1rem',
	transition: 'background 250ms',
	whiteSpace: 'nowrap',

	[media.below.sm]: {
		fontSize: '0.875rem',
	},
});
